import React from "react";
import { NODE_META, slotTarget, nodeById, insertNode, removeNode, moveNode, parentSlotOf, sameSlot, nodeTitle, nodeSubtitle, delayDuration, conditionText, tagListName } from "./common";
import { num } from "../email-automation/common";

/*
 * StepTree — the journey graph as nested step columns. Trees only (v1):
 * a branch splits into a Yes column and a No column; nothing rejoins, so
 * every node has exactly one slot above it and the tree reads top-down
 * like a checklist. Editable when `onChange` is given (add/remove steps,
 * select a step to edit it in the panel below); read-only otherwise, with
 * the per-node funnel from `stats` overlaid.
 *
 * Read-only (no `onChange`, the journey page; Email redesign 2026-10-09 mockup Journey.dc.html):
 * a centred vertical step map instead. Trigger pill, email cards with sent / opened / clicked
 * on the right and an amber "N people stopped before this email" line, wait pills, branches as
 * Yes / No columns, and "Finished · N" at the end. Extra props: `finished` (count for the end pill).
 */

const slotKey = slot => slot ? `${slot.parentId}:${slot.edge}` : "start";
const pct = (n, d) => Number(d) > 0 ? `${Math.round((Number(n) || 0) / Number(d) * 100)}%` : "—";
export default class StepTree extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      menu: null,
      confirmRemove: null,
      dragging: null,
      over: null
    };
  }

  // ---- drag and drop (email / wait steps only) ----
  canDrag(n) {
    return !!this.props.onChange && (n.type === "email" || n.type === "delay");
  }
  dragStart(n, e) {
    try {
      e.dataTransfer.setData("text/plain", n.id);
      e.dataTransfer.effectAllowed = "move";
    } catch (x) {/* older engines */}
    this.setState({
      dragging: n.id,
      menu: null,
      confirmRemove: null
    });
  }
  dragEnd() {
    this.setState({
      dragging: null,
      over: null
    });
  }
  dropTarget(slot) {
    // A slot is a valid target unless it is the dragged step's own position
    // or the slot right below it (both are no-ops).
    const {
      dragging
    } = this.state;
    if (!dragging) return null;
    if (slot && slot.parentId === dragging) return null;
    if (sameSlot(parentSlotOf(this.props.graph, dragging), slot)) return null;
    const key = slotKey(slot);
    const on = this.state.over === key;
    return /*#__PURE__*/React.createElement("div", {
      onDragOver: e => {
        e.preventDefault();
        try {
          e.dataTransfer.dropEffect = "move";
        } catch (x) {/* noop */}
        if (!on) this.setState({
          over: key
        });
      },
      onDragLeave: () => {
        if (on) this.setState({
          over: null
        });
      },
      onDrop: e => {
        e.preventDefault();
        const id = this.state.dragging;
        this.setState({
          dragging: null,
          over: null
        });
        if (id) this.props.onChange(moveNode(this.props.graph, id, slot));
      },
      style: {
        margin: "0 0 0 14px",
        height: on ? 34 : 22,
        border: `2px dashed ${on ? "var(--plk-accent)" : "var(--plk-border)"}`,
        borderRadius: 8,
        background: on ? "var(--plk-accent-soft)" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        color: on ? "var(--plk-accent)" : "var(--plk-text-dim)",
        transition: "height .1s",
        maxWidth: 360
      }
    }, on ? "Drop here" : "");
  }
  add(slot, type) {
    const {
      graph,
      id
    } = insertNode(this.props.graph, slot, type);
    this.setState({
      menu: null
    });
    this.props.onChange(graph);
    if (this.props.onSelect) this.props.onSelect(id);
  }
  remove(id) {
    const n = nodeById(this.props.graph, id);
    if (n && n.type === "branch" && this.state.confirmRemove !== id) {
      this.setState({
        confirmRemove: id
      });
      return;
    }
    this.setState({
      confirmRemove: null,
      menu: null
    });
    this.props.onChange(removeNode(this.props.graph, id));
    if (this.props.onSelect && this.props.selected === id) this.props.onSelect(null);
  }
  addMenu(slot, atEnd) {
    const editable = !!this.props.onChange;
    if (!editable) {
      return atEnd ? /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 11.5,
          color: "var(--plk-text-dim)",
          padding: "2px 0 6px 14px"
        }
      }, "\u2193 completes") : null;
    }
    if (this.state.dragging) return /*#__PURE__*/React.createElement("div", {
      style: {
        padding: atEnd ? "2px 0 8px 0" : 0
      }
    }, this.dropTarget(slot));
    const key = slotKey(slot);
    const open = this.state.menu === key;
    const types = atEnd ? ["email", "delay", "branch", "exit"] : ["email", "delay", "branch"];
    return /*#__PURE__*/React.createElement("div", {
      style: {
        padding: atEnd ? "2px 0 8px 14px" : "0 0 0 14px",
        display: "flex",
        alignItems: "center",
        gap: 6,
        minHeight: 22
      }
    }, !open ? /*#__PURE__*/React.createElement("a", {
      className: "mtf-chip",
      style: {
        cursor: "pointer",
        fontSize: 11.5,
        padding: atEnd ? undefined : "0 7px",
        opacity: atEnd ? 1 : 0.75
      },
      title: atEnd ? "Add a step here" : "Insert a step here",
      onClick: () => this.setState({
        menu: key,
        confirmRemove: null
      })
    }, atEnd ? "＋ Add step" : "＋") : /*#__PURE__*/React.createElement(React.Fragment, null, types.map(t => /*#__PURE__*/React.createElement("a", {
      key: t,
      className: "mtf-chip",
      style: {
        cursor: "pointer",
        fontSize: 11.5,
        color: NODE_META[t].color
      },
      onClick: () => this.add(slot, t)
    }, t === "delay" ? "Wait" : t === "exit" ? "End" : NODE_META[t].label)), /*#__PURE__*/React.createElement("a", {
      className: "mtf-chip",
      style: {
        cursor: "pointer",
        fontSize: 11.5,
        color: "var(--plk-text-dim)"
      },
      onClick: () => this.setState({
        menu: null
      })
    }, "cancel")));
  }
  card(n, prev) {
    const {
      selected,
      onSelect,
      stats,
      audiences,
      onChange
    } = this.props;
    const meta = NODE_META[n.type] || NODE_META.exit;
    const on = selected === n.id;
    // Always show counts once the funnel is loaded (DS 2026-10-04): a step nobody reached reads "0 reached", and every
    // step shows how many left before it compared with the previous step (or with enrolled, for the first step).
    const s = stats ? stats[n.id] || {
      entered: 0,
      sent: 0,
      opened: 0,
      clicked: 0,
      failed: 0,
      unsubscribed: 0
    } : null;
    const entered = s ? Number(s.entered) || 0 : 0;
    const drop = s && prev != null && prev > entered ? prev - entered : 0;
    const confirming = this.state.confirmRemove === n.id;
    const isExit = n.type === "exit";
    const draggable = this.canDrag(n);
    const isDragging = this.state.dragging === n.id;
    return /*#__PURE__*/React.createElement("div", {
      draggable: draggable,
      onDragStart: draggable ? e => this.dragStart(n, e) : undefined,
      onDragEnd: draggable ? () => this.dragEnd() : undefined,
      title: draggable ? "Drag to move this step" : undefined,
      style: {
        opacity: isDragging ? 0.45 : 1,
        border: `1px solid ${on ? "var(--plk-accent)" : "var(--plk-border)"}`,
        boxShadow: on ? "0 0 0 2px var(--plk-accent-soft)" : "none",
        borderRadius: 10,
        background: isExit ? "var(--plk-surface-alt)" : "#fff",
        padding: "8px 10px",
        cursor: onSelect ? "pointer" : "default",
        minWidth: 220,
        maxWidth: 360,
        userSelect: "none",
        borderStyle: isExit ? "dashed" : "solid"
      },
      onClick: () => onSelect && onSelect(on ? null : n.id)
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8,
        alignItems: "flex-start"
      }
    }, draggable ? /*#__PURE__*/React.createElement("span", {
      style: {
        color: "var(--plk-text-dim)",
        fontSize: 13,
        lineHeight: 1,
        marginTop: 3,
        cursor: "grab",
        flex: "0 0 auto"
      },
      title: "Drag to move"
    }, "\u22EE\u22EE") : null, /*#__PURE__*/React.createElement("span", {
      className: "cust-stage",
      style: {
        background: meta.bg,
        color: meta.color,
        flex: "0 0 auto"
      }
    }, n.type === "delay" ? "wait" : n.type === "exit" ? "end" : n.type), /*#__PURE__*/React.createElement("div", {
      style: {
        minWidth: 0,
        flex: 1
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 12.5,
        fontWeight: 600,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    }, nodeTitle(n, audiences)), nodeSubtitle(n) ? /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11.5,
        color: "var(--plk-text-muted)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    }, nodeSubtitle(n)) : null, s ? /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11,
        color: "var(--plk-text-dim)",
        marginTop: 3
      }
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        color: "var(--plk-text)"
      }
    }, num(entered), " reached"), drop > 0 ? /*#__PURE__*/React.createElement("span", {
      style: {
        color: "var(--plk-warning-text, #b7791f)"
      }
    }, ` · −${num(drop)} (${pct(drop, prev)}) left before this step`) : prev != null && prev > 0 && entered === prev ? " · everyone from the previous step" : "", n.type === "email" ? /*#__PURE__*/React.createElement(React.Fragment, null, ` · ${num(s.sent)} sent`, this.props.engagement !== false ? ` · ${num(s.opened || 0)} opened${s.sent > 0 ? ` (${pct(s.opened, s.sent)})` : ""} · ${num(s.clicked || 0)} clicked${s.sent > 0 ? ` (${pct(s.clicked, s.sent)})` : ""}` : "", s.failed ? ` · ${num(s.failed)} failed` : "", s.unsubscribed ? ` · ${num(s.unsubscribed)} unsubscribed` : "") : null) : null), onChange ? /*#__PURE__*/React.createElement("span", {
      onClick: e => e.stopPropagation(),
      style: {
        flex: "0 0 auto"
      }
    }, confirming ? /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 11,
        display: "inline-flex",
        gap: 4,
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement("span", null, "Remove both paths?"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      style: {
        marginTop: 0,
        padding: "1px 7px",
        color: "var(--plk-danger-text)"
      },
      onClick: () => this.remove(n.id)
    }, "Yes"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      style: {
        marginTop: 0,
        padding: "1px 7px"
      },
      onClick: () => this.setState({
        confirmRemove: null
      })
    }, "No")) : /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      style: {
        marginTop: 0,
        padding: "1px 7px"
      },
      title: n.type === "branch" ? "Remove this branch and everything below it" : "Remove this step",
      onClick: () => this.remove(n.id)
    }, "\xD7")) : null));
  }
  connector() {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        width: 2,
        height: 10,
        background: "var(--plk-border)",
        marginLeft: 20
      }
    });
  }
  path(slot, depth, prev) {
    const {
      graph
    } = this.props;
    const id = slotTarget(graph, slot);
    const n = id ? nodeById(graph, id) : null;
    if (!n) return this.addMenu(slot, true);
    if (depth > 60) return null;
    const editable = !!this.props.onChange;
    const here = this.props.stats ? Number((this.props.stats[n.id] || {}).entered) || 0 : null;
    return /*#__PURE__*/React.createElement("div", null, this.card(n, prev), n.type === "branch" ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        marginTop: 6,
        overflowX: "auto"
      }
    }, [["yes", "Yes", "var(--plk-success-text)"], ["no", "No", "var(--plk-danger-text)"]].map(([edge, label, color]) => /*#__PURE__*/React.createElement("div", {
      key: edge,
      style: {
        flex: "1 1 240px",
        minWidth: 240,
        borderLeft: `2px solid ${color}`,
        paddingLeft: 10
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11,
        fontWeight: 700,
        color,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        margin: "2px 0 6px"
      }
    }, label), this.path({
      parentId: n.id,
      edge
    }, depth + 1, here)))) : n.type === "exit" ? null : /*#__PURE__*/React.createElement(React.Fragment, null, this.connector(), editable && n.nextNodeId ? this.addMenu({
      parentId: n.id,
      edge: "next"
    }, false) : null, editable && n.nextNodeId ? this.connector() : null, this.path({
      parentId: n.id,
      edge: "next"
    }, depth + 1, here)));
  }

  // ---- read-only step map (journey page) ----
  mapEmail(n, base) {
    const {
      stats,
      audiences,
      engagement
    } = this.props;
    const s = stats ? stats[n.id] || {
      entered: 0,
      sent: 0,
      opened: 0,
      clicked: 0,
      failed: 0,
      unsubscribed: 0
    } : null;
    const entered = s ? Number(s.entered) || 0 : 0;
    const reached = s ? Math.max(entered, Number(s.sent) || 0) : 0;
    const drop = s && base != null && base > reached ? base - reached : 0;
    const sub = nodeSubtitle(n);
    return /*#__PURE__*/React.createElement("div", {
      className: "ejl-map-email"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ejl-map-icon",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "3",
      y: "5",
      width: "18",
      height: "14",
      rx: "2"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M3 7l9 6 9-6"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ejl-map-main"
    }, /*#__PURE__*/React.createElement("b", null, nodeTitle(n, audiences)), drop > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "warn"
    }, num(drop), " ", drop === 1 ? "person" : "people", " stopped before this email (", pct(drop, base), ")") : sub && sub !== "composed" ? /*#__PURE__*/React.createElement("span", null, sub) : null), s ? /*#__PURE__*/React.createElement("div", {
      className: "ejl-map-nums"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, num(s.sent)), " sent"), engagement !== false ? /*#__PURE__*/React.createElement("span", null, num(s.opened || 0), " opened \xB7 ", num(s.clicked || 0), " clicked") : null, Number(s.failed) > 0 || Number(s.unsubscribed) > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, [Number(s.failed) > 0 ? `${num(s.failed)} not sent` : null, Number(s.unsubscribed) > 0 ? `${num(s.unsubscribed)} unsubscribed` : null].filter(Boolean).join(" · ")) : null) : null);
  }
  mapLine() {
    return /*#__PURE__*/React.createElement("span", {
      className: "ejl-map-line",
      "aria-hidden": "true"
    });
  }

  // Renders one path; returns elements. `base` = people who reached the last email (or joined).
  mapPath(id, base, depth, top) {
    const {
      graph,
      stats,
      audiences
    } = this.props;
    const out = [];
    const seen = new Set();
    let cur = id;
    let b = base;
    while (cur && !seen.has(cur) && depth < 60) {
      seen.add(cur);
      const n = nodeById(graph, cur);
      if (!n || n.type === "exit") break;
      out.push(/*#__PURE__*/React.createElement(React.Fragment, {
        key: `l-${n.id}`
      }, this.mapLine()));
      if (n.type === "email") {
        out.push(/*#__PURE__*/React.createElement(React.Fragment, {
          key: n.id
        }, this.mapEmail(n, b)));
        if (stats) {
          const s = stats[n.id] || {};
          b = Math.max(Number(s.entered) || 0, Number(s.sent) || 0);
        }
        cur = n.nextNodeId;
      } else if (n.type === "delay") {
        const d = delayDuration(n);
        out.push(/*#__PURE__*/React.createElement("div", {
          key: n.id,
          className: "ejl-map-wait"
        }, /*#__PURE__*/React.createElement("svg", {
          width: "14",
          height: "14",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          "aria-hidden": "true"
        }, /*#__PURE__*/React.createElement("circle", {
          cx: "12",
          cy: "12",
          r: "9"
        }), /*#__PURE__*/React.createElement("path", {
          d: "M12 7v5l3 2"
        })), d ? `Wait ${d}` : "Wait"));
        cur = n.nextNodeId;
      } else if (n.type === "tag") {
        out.push(/*#__PURE__*/React.createElement("div", {
          key: n.id,
          className: "ejl-map-tag"
        }, /*#__PURE__*/React.createElement("svg", {
          width: "14",
          height: "14",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          "aria-hidden": "true"
        }, /*#__PURE__*/React.createElement("path", {
          d: "M3 12V4a1 1 0 011-1h8l9 9-9 9-9-9z"
        }), /*#__PURE__*/React.createElement("circle", {
          cx: "7.5",
          cy: "7.5",
          r: "1.5"
        })), "Added to ", tagListName(n, audiences)));
        cur = n.nextNodeId;
      } else if (n.type === "branch") {
        const here = stats ? Number((stats[n.id] || {}).entered) || 0 : null;
        out.push(/*#__PURE__*/React.createElement("div", {
          key: n.id,
          className: "ejl-map-branch"
        }, n.name || conditionText(n, audiences), here != null ? /*#__PURE__*/React.createElement("span", {
          className: "dim"
        }, " \xB7 ", num(here), " checked") : null));
        out.push(/*#__PURE__*/React.createElement("div", {
          key: `${n.id}-paths`,
          className: "ejl-map-split"
        }, [["yes", "Yes", n.yesNodeId], ["no", "No", n.noNodeId]].map(([edge, label, next]) => /*#__PURE__*/React.createElement("div", {
          key: edge,
          className: `ejl-map-col ${edge}`
        }, this.mapLine(), /*#__PURE__*/React.createElement("span", {
          className: "ejl-map-edge"
        }, label), this.mapPath(next, b, depth + 1, false)))));
        return out;
      } else {
        cur = n.nextNodeId;
      }
      depth++;
    }
    out.push(/*#__PURE__*/React.createElement(React.Fragment, {
      key: "end-l"
    }, this.mapLine()));
    out.push(top && this.props.finished != null ? /*#__PURE__*/React.createElement("div", {
      key: "end",
      className: "ejl-map-end"
    }, "Finished \xB7 ", num(this.props.finished)) : /*#__PURE__*/React.createElement("div", {
      key: "end",
      className: "ejl-map-end"
    }, top ? "Finished" : "Ends here"));
    return out;
  }
  renderMap() {
    const {
      graph,
      triggerLabel,
      stats,
      enrolled
    } = this.props;
    const hasSteps = (graph.nodes || []).length > 0;
    return /*#__PURE__*/React.createElement("div", {
      className: "ejl-map"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ejl-map-trigger"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "15",
      height: "15",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M13 2L3 14h9l-1 8 10-12h-9z"
    })), /*#__PURE__*/React.createElement("b", null, triggerLabel || "Someone joins"), stats ? /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, num(enrolled), " joined") : null), hasSteps ? this.mapPath(graph.startNodeId, stats ? Number(enrolled) || 0 : null, 0, true) : /*#__PURE__*/React.createElement("p", {
      className: "ejl-map-empty"
    }, "No steps yet. Open Edit to add the first email."));
  }
  render() {
    if (!this.props.onChange) return this.renderMap();
    const {
      graph,
      triggerLabel
    } = this.props;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        overflowX: "auto"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 6
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "cust-stage",
      style: {
        background: "var(--plk-success-soft)",
        color: "var(--plk-success-text)"
      }
    }, "trigger"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5
      }
    }, triggerLabel || "Enrolls a contact")), this.connector(), this.path(null, 0, this.props.stats ? Number(this.props.enrolled) || 0 : null), (graph.nodes || []).length ? null : /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "6px 0 0"
      }
    }, "Add the first step: usually an email, or a wait if the trigger fires too early to write."));
  }
}